import { useMemo, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useEditMode,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus } from 'lucide-react';

const FILTERS = [
  'All',
  'C-LEVEL Team',
  'Executive Team',
  'Operations',
  'Investment',
  'Capital Markets',
] as const;

type Filter = (typeof FILTERS)[number];

const PORTRAIT =
  '';

const member1Def: SlotGroupDefinition = {
  label: 'Team Member 1',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Joe Lubeck' },
    role: { label: 'Role', type: 'text', defaultValue: 'Founder & Chief Executive Officer' },
    dept: { label: 'Department', type: 'text', defaultValue: 'C-LEVEL Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        "Joe is the Founder and CEO of American Landmark and is the chair of American Landmark's Senior Executive Management Committee and the chair of the Investment Committee.",
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/dac7e7d5/dms3rep/multi/Joe_icvtte.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Joe Lubeck' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/joe-lubeck' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member2Def: SlotGroupDefinition = {
  label: 'Team Member 2',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Jim Miller' },
    role: { label: 'Role', type: 'text', defaultValue: 'President & Chief Financial Officer' },
    dept: { label: 'Department', type: 'text', defaultValue: 'C-LEVEL Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Jim is the President and CFO of American Landmark and a member of the Senior Executive Management Committee, co-chair of the Valuation Committee.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/dac7e7d5/dms3rep/multi/Jim_muffkg.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Jim Miller' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2a4a6f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/jim-miller' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member3Def: SlotGroupDefinition = {
  label: 'Team Member 3',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Elizabeth Roy' },
    role: { label: 'Role', type: 'text', defaultValue: 'Chief Investment Officer' },
    dept: { label: 'Department', type: 'text', defaultValue: 'C-LEVEL Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        "Elizabeth Roy serves as CIO of American Landmark, where she leads the firm's investment strategy, capital deployment, and debt management functions.",
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/dac7e7d5/dms3rep/multi/Elizabeth+%281%29.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Elizabeth Roy' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#3d5a80]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/elizabeth-roy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member4Def: SlotGroupDefinition = {
  label: 'Team Member 4',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Andrew Yam' },
    role: {
      label: 'Role',
      type: 'text',
      defaultValue: 'Sr. Managing Director, Global Head of Capital Markets',
    },
    dept: { label: 'Department', type: 'text', defaultValue: 'C-LEVEL Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        "Andrew serves as Global Head of Capital Markets at American Landmark and is a member of the firm's Investment Committee and Senior Executive Management Committee.",
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/dac7e7d5/dms3rep/multi/Andrew+Yam+%281%29.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Andrew Yam' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0a1628] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/andrew-yam' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member5Def: SlotGroupDefinition = {
  label: 'Team Member 5',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Stacey Bondar' },
    role: {
      label: 'Role',
      type: 'text',
      defaultValue: 'Chief Operating Officer for Eastern Region Operations',
    },
    dept: { label: 'Department', type: 'text', defaultValue: 'C-LEVEL Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Stacey serves as Chief Operating Officer of the East Coast (Florida, Georgia, Virginia, North Carolina and South Carolina) for American Landmark, and is Member of Senior Executive Management Committee.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/dac7e7d5/dms3rep/multi/Stacey_pa6w8r.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Stacey Bondar' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#4a6fa5]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/stacey-bondar' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member49Def: SlotGroupDefinition = {
  label: 'Team Member 49',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'David Deitz' },
    role: {
      label: 'Role',
      type: 'text',
      defaultValue: 'Chief Operating Officer for Western Region Operations',
    },
    dept: { label: 'Department', type: 'text', defaultValue: 'C-LEVEL Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'David serves as Chief Operating Officer of the West Region for American Landmark, overseeing operations across the western portfolio.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'David Deitz' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#3d5a80]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/david-deitz' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member7Def: SlotGroupDefinition = {
  label: 'Team Member 7',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Rachel Palmer' },
    role: { label: 'Role', type: 'text', defaultValue: 'Chief Administrative Officer' },
    dept: { label: 'Department', type: 'text', defaultValue: 'C-LEVEL Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Rachel serves as our CAO for American Landmark, and is a Member of Senior Executive Management Committee, Member of ESG Committee.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/dms3rep/multi/Circle_Headshots_for_Website_5_uz49bt.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Rachel Palmer' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/rachel-palmer' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member9Def: SlotGroupDefinition = {
  label: 'Team Member 9',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Hale McNinch' },
    role: { label: 'Role', type: 'text', defaultValue: 'Chief Revenue Officer' },
    dept: { label: 'Department', type: 'text', defaultValue: 'C-LEVEL Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Hale McNinch serves as the EVP of Revenue Management for American Landmark, and is a Member of Senior Executive Management Committee.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/dac7e7d5/dms3rep/multi/Hale_gzd6k9.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Hale McNinch' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#3d5a80]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/hale-mcninch' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member10Def: SlotGroupDefinition = {
  label: 'Team Member 10',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Adam Smolyar' },
    role: { label: 'Role', type: 'text', defaultValue: 'Chief Innovation & Technology Officer' },
    dept: { label: 'Department', type: 'text', defaultValue: 'C-LEVEL Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Adam serves as the Chief Innovation and Technology Officer of American Landmark, and is a Member of Senior Executive Management Committee.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue: 'https://irp.cdn-website.com/dac7e7d5/dms3rep/multi/Adam+Smolyar+%283%29.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Adam Smolyar' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2a4a6f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/adam-smolyar' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member11Def: SlotGroupDefinition = {
  label: 'Team Member 11',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Lucinda Figueroa' },
    role: {
      label: 'Role',
      type: 'text',
      defaultValue: 'Senior Managing Director of Human Resources',
    },
    dept: { label: 'Department', type: 'text', defaultValue: 'Executive Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Lucinda Figueroa serves as the Senior Managing Director of Human Resources. She brings over 20 years of recruiting, consulting and talent acquisition experience.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Lucinda Figueroa' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#2a4a6f]' },
    linkedin: { label: 'LinkedIn URL', type: 'url', defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/' },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/lucinda-figueroa' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member12Def: SlotGroupDefinition = {
  label: 'Team Member 12',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Colby Robertson' },
    role: { label: 'Role', type: 'text', defaultValue: 'EVP of Eastern Operations' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Executive Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Colby is the EVP for Eastern markets for American Landmark and a member of Senior Executive Management Committee.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Colby Robertson' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#3d5a80]' },
    linkedin: { label: 'LinkedIn URL', type: 'url', defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/' },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/colby-robertson' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member13Def: SlotGroupDefinition = {
  label: 'Team Member 13',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Lee Brown' },
    role: { label: 'Role', type: 'text', defaultValue: 'EVP of Renovation Construction' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Executive Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Lee serves as Executive Vice President of Renovation Construction for American Landmark, overseeing major capital projects, renovations, and insurance claims across the company\'s western portfolio.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Lee Brown' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]' },
    linkedin: { label: 'LinkedIn URL', type: 'url', defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/' },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/lee-brown' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member14Def: SlotGroupDefinition = {
  label: 'Team Member 14',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'JR Agostini' },
    role: {
      label: 'Role',
      type: 'text',
      defaultValue: 'EVP of Renovation Construction (East Region)',
    },
    dept: { label: 'Department', type: 'text', defaultValue: 'Executive Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'JR Agostini serves as Executive Vice President of Renovation Construction for the East Region at American Landmark, bringing more than 25 years of multifamily experience.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'JR Agostini' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0a1628] to-[#2c5282]' },
    linkedin: { label: 'LinkedIn URL', type: 'url', defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/' },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/jr-agostini' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member15Def: SlotGroupDefinition = {
  label: 'Team Member 15',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Patrick Manyeki' },
    role: {
      label: 'Role',
      type: 'text',
      defaultValue: 'VP of Asset Management, Corporate Sustainability/ESG',
    },
    dept: { label: 'Department', type: 'text', defaultValue: 'Executive Team' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Patrick serves as Vice President of Asset Management. In this capacity he is responsible for asset management and compliance functions across the portfolio.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Patrick Manyeki' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#4a6fa5]' },
    linkedin: { label: 'LinkedIn URL', type: 'url', defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/' },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/patrick-manyeki' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member16Def: SlotGroupDefinition = {
  label: 'Team Member 16',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Dana Hammond' },
    role: { label: 'Role', type: 'text', defaultValue: 'Director of Training' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Dana brings over 20 years of property management expertise to her role, including 15 years experience in the training and education fields.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Dana Hammond' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/dana-hammond' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member17Def: SlotGroupDefinition = {
  label: 'Team Member 17',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Devin Lusk' },
    role: { label: 'Role', type: 'text', defaultValue: 'Director of Marketing' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Boasting more than 15 years of multifamily housing industry experience, Devin brings dynamic experience and knowledge to the American Landmark team as Director of Marketing.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Devin Lusk' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2a4a6f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/devin-lusk' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member18Def: SlotGroupDefinition = {
  label: 'Team Member 18',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Julie Doll' },
    role: { label: 'Role', type: 'text', defaultValue: 'Director of Systems & Solutions' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Julie Doll is the Director of Systems & Solutions at American Landmark Apartments and brings more than 35 years of experience in the property management industry.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Julie Doll' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#3d5a80]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/julie-doll' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member19Def: SlotGroupDefinition = {
  label: 'Team Member 19',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Yvonna Groteke' },
    role: { label: 'Role', type: 'text', defaultValue: 'Senior Director of Payroll Operations' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Yvonna Groteke serves as the Senior Director of Payroll Operations.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Yvonna Groteke' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0a1628] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/yvonna-groteke' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member20Def: SlotGroupDefinition = {
  label: 'Team Member 20',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Daniel Looft' },
    role: { label: 'Role', type: 'text', defaultValue: 'Director of Talent Acquisitions' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Daniel is responsible for implementing recruitment processes and strategies for American Landmark Apartments.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Daniel Looft' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#4a6fa5]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/daniel-looft' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member21Def: SlotGroupDefinition = {
  label: 'Team Member 21',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Codi Stepanski' },
    role: { label: 'Role', type: 'text', defaultValue: 'Sr Regional VP | Houston Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Codi has over 28 years of experience in the Multi-Family Industry holding multiple areas of knowledge within this field.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Codi Stepanski' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2c5282]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/codi-stepanski' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member22Def: SlotGroupDefinition = {
  label: 'Team Member 22',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'David Smith' },
    role: { label: 'Role', type: 'text', defaultValue: 'Sr Regional VP | Nashville & DFW Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'David brings over 20 years of experience in ground-up development, re-development, and stabilized assets.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'David Smith' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/david-smith' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member23Def: SlotGroupDefinition = {
  label: 'Team Member 23',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Lisa Santiago' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional VP | FL, GA, SC Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Prior to joining American Landmark Lisa Santiago served as Vice President of Operations for Invitation Homes.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Lisa Santiago' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0a1628] to-[#274c77]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/lisa-santiago' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member24Def: SlotGroupDefinition = {
  label: 'Team Member 24',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Lissette Perez' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional VP | FL, NC & VA Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Lissette spearheads the Orlando and Virginia market with her extensive background in Servant Leadership.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Lissette Perez' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#3d5a80]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/lissette-perez' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member25Def: SlotGroupDefinition = {
  label: 'Team Member 25',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Shannon Lee' },
    role: { label: 'Role', type: 'text', defaultValue: 'Senior Regional Manager | San Antonio Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Shannon Lee joins American Landmark as Senior Regional Manager, bringing more than 20 years of multifamily leadership experience across Texas.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Shannon Lee' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2a4a6f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/shannon-lee' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member26Def: SlotGroupDefinition = {
  label: 'Team Member 26',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Amy Cox' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Dallas/Ft Worth Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'With over two decades of property management expertise, Amy commenced her career in 1998 during nursing school, until she decided healthcare was not her path.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Amy Cox' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/amy-cox' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member27Def: SlotGroupDefinition = {
  label: 'Team Member 27',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Anna Burgess' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Nashville Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Anna oversees our Tennessee portfolio as Regional Manager and has over 9 years of experience in the multifamily management industry.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Anna Burgess' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2a4a6f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/anna-burgess' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member28Def: SlotGroupDefinition = {
  label: 'Team Member 28',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Cortney Myers' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Houston Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Cortney is currently serving as a Regional Manager in Houston, Texas.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Cortney Myers' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#3d5a80]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/cortney-myers' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member29Def: SlotGroupDefinition = {
  label: 'Team Member 29',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Erik Mancol-Bilbo' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Dallas / Ft Worth Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Erik Mancol-Bilbo is a Certified Apartment Manager (CAM) with more than 17 years of multifamily experience across ground up development, lease ups, redevelopment, stabilized communities, and Affordable AMI and PFC housing programs.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Erik Mancol-Bilbo' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0a1628] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/erik-mancol-bilbo' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member30Def: SlotGroupDefinition = {
  label: 'Team Member 30',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Fidel Lozano' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Houston Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Fidel has been in the industry for over 20 years, briefly leaving for 2 years and then persuaded to return.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Fidel Lozano' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#4a6fa5]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/fidel-lozano' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member31Def: SlotGroupDefinition = {
  label: 'Team Member 31',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Jennifer Hendrix' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Charlotte Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Jennifer serves as a Regional Manager in the Charlotte, NC market, and has been with American Landmark for 4 years.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Jennifer Hendrix' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2c5282]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/jennifer-hendrix' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member32Def: SlotGroupDefinition = {
  label: 'Team Member 32',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Kara Brown' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Houston Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Kara is a passionate and dynamic leader with an extensive track record spanning over 35 years in the multi family industry.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Kara Brown' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/kara-brown' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member34Def: SlotGroupDefinition = {
  label: 'Team Member 34',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Nakia Ellis' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Orlando & Virginia Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Nakia serves as the Regional Manager for our Orlando region.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Nakia Ellis' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#3d5a80]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/nakia-ellis' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member35Def: SlotGroupDefinition = {
  label: 'Team Member 35',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Sara Lowenthal' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Tampa Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Sara serves as the Regional Manager for our Tampa region.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Sara Lowenthal' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2a4a6f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/sara-lowenthal' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member36Def: SlotGroupDefinition = {
  label: 'Team Member 36',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Scarlette Pereira' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | S Florida & Melbourne Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Scarlette is a seasoned professional with over 20 years of invaluable experience in property management.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Scarlette Pereira' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/scarlette-pereira' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member37Def: SlotGroupDefinition = {
  label: 'Team Member 37',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Sheri Brooks' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Atlanta Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Sheri serves as the Regional Manager for Georgia and North Carolina.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Sheri Brooks' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2a4a6f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/sheri-brooks' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member38Def: SlotGroupDefinition = {
  label: 'Team Member 38',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Ashley Watts' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Manager | Jacksonville Market' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'With over nine years of experience in the multifamily industry, Ashley has built her career from the ground up, beginning in leasing and rising through the ranks with persistence, adaptability, and a passion for people.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Ashley Watts' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#3d5a80]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/ashley-watts' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member39Def: SlotGroupDefinition = {
  label: 'Team Member 39',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Kelsie Jezak' },
    role: { label: 'Role', type: 'text', defaultValue: 'Regional Support Specialist | East Communities' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Operations' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'After more than seven years with American Landmark, Kelsie currently serves as the role of Regional Support Specialist for the East Region.',
    },
    image: { label: 'Photo', type: 'image', defaultValue:
      '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Kelsie Jezak' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0a1628] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/kelsie-jezak' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member40Def: SlotGroupDefinition = {
  label: 'Team Member 40',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Bulent Yazar' },
    role: { label: 'Role', type: 'text', defaultValue: 'Managing Director of Acquisitions' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Investment' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Bulent serves as the Managing Director of Acquisitions for American Landmark, and is a Member of Senior Executive Management Committee.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Bulent Yazar' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/bulent-yazar' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member41Def: SlotGroupDefinition = {
  label: 'Team Member 41',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Cole Malizia' },
    role: { label: 'Role', type: 'text', defaultValue: 'Director of Dispositions' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Investment' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Cole is the Director of Dispositions for American Landmark. He is responsible for the evaluation and sale of assets from the firm\'s various funds.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Cole Malizia' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2a4a6f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/cole-malizia' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member42Def: SlotGroupDefinition = {
  label: 'Team Member 42',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Noah Brown' },
    role: { label: 'Role', type: 'text', defaultValue: 'Manager of Acquisitions' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Investment' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Noah serves as the Manager of Acquisitions for American Landmark. Noah has been working at American Landmark since June of 2021 and has been involved in the underwriting and diligence of over $3.5Bn worth of multifamily acquisitions representing over 15,000 units.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Noah Brown' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#3d5a80]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/noah-brown' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member43Def: SlotGroupDefinition = {
  label: 'Team Member 43',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Jessica Wichser' },
    role: { label: 'Role', type: 'text', defaultValue: 'Senior Executive Portfolio Manager' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Jessica serves as Senior Executive Portfolio Manager for American Landmark, where she is responsible for overseeing portfolio performance and strategy for the firm\'s multifamily investment entities.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Jessica Wichser' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/jessica-wichser' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member44Def: SlotGroupDefinition = {
  label: 'Team Member 44',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Aharon Dinovitz' },
    role: { label: 'Role', type: 'text', defaultValue: 'Managing Director of Private Wealth' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Aharon serves as a trusted advisor to high net worth families, overseeing family offices and deploying equity with a focus on multifamily real estate.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Aharon Dinovitz' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0c1a2e] to-[#2a4a6f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/aharon-dinovitz' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member45Def: SlotGroupDefinition = {
  label: 'Team Member 45',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Tom Lubeck' },
    role: { label: 'Role', type: 'text', defaultValue: 'Director of Capital Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Tom Lubeck serves as Director of Capital Markets at American Landmark in the firm\'s New York City office.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Tom Lubeck' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#3d5a80]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/tom-lubeck' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member46Def: SlotGroupDefinition = {
  label: 'Team Member 46',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Siara Rodriguez-Mirabel' },
    role: { label: 'Role', type: 'text', defaultValue: 'Vice President, Capital Markets' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Siara Rodriguez-Mirabel joined American Landmark in 2021 and played an integral role in the success of the firm\'s fourth fundraise, which surpassed its target of $975M closing just over $1B of capital.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Siara Rodriguez-Mirabel' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0a1628] to-[#1e3a5f]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: {
      label: 'Bio Page URL',
      type: 'url',
      defaultValue: '/team/siara-rodriguez-mirabel',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member47Def: SlotGroupDefinition = {
  label: 'Team Member 47',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Ali Serrhini' },
    role: { label: 'Role', type: 'text', defaultValue: 'Managing Director, MENA' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Capital Markets' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Ali serves as Managing Director, MENA for American Landmark, leading the firm\'s investor relationships across the Middle East and North Africa.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Ali Serrhini' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[rgb(var(--ala-dark))] to-[#2c5282]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/ali-serrhini' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const member48Def: SlotGroupDefinition = {
  label: 'Team Member 48',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Greta Ivezaj' },
    role: { label: 'Role', type: 'text', defaultValue: 'Investment Management Coordinator' },
    dept: { label: 'Department', type: 'text', defaultValue: 'Investment' },
    bio: {
      label: 'Bio',
      type: 'text',
      defaultValue:
        'Greta serves as Investment Management Coordinator for American Landmark, supporting the investment team across the firm\'s multifamily portfolio.',
    },
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Greta Ivezaj' },
    accent: { label: 'Accent Gradient', type: 'text', defaultValue: 'from-[#0a1628] to-[#274c77]' },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    bio_url: { label: 'Bio Page URL', type: 'url', defaultValue: '/team/greta-ivezaj' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

interface TeamMember {
  id: string;
  name: string;
  role: string;
  dept: string;
  bio: string;
  image: string;
  alt: string;
  accent: string;
  linkedin: string;
  bioUrl: string;
  target: string;
  circleCrop: boolean;
}

function TeamCard({ member }: { member: TeamMember }) {
  const href = member.bioUrl || '/team';

  return (
    <div className="group relative rounded-2xl bg-[rgb(var(--ala-background))] shadow-sm transition-shadow duration-300 hover:shadow-2xl">
      <div
        className="relative aspect-[3/4] overflow-hidden bg-[#0A1626] [--notch:40px] sm:[--notch:56px]"
        style={{
          clipPath:
            'polygon(0 0, 100% 0, 100% calc(100% - var(--notch)), calc(100% - var(--notch)) 100%, 0 100%)',
          borderRadius: '16px',
        }}
      >
        {/* Built-in default headshots are circle-cropped PNGs; zoom until the circle edge clears the 3:4 frame. */}
        <div className={`absolute inset-0 ${member.circleCrop ? 'scale-[1.42]' : ''}`}>
          <VibesitesImage
            url={member.image || PORTRAIT}
            alt={member.alt || member.name || ''}
            sizes="(max-width: 1024px) 50vw, 25vw"
            className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-105"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div
          className={`absolute inset-0 bg-gradient-to-br ${member.accent || 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]'} opacity-0 mix-blend-overlay transition-opacity duration-500 group-hover:opacity-20`}
        />
        <div className="absolute bottom-0 left-0 right-10 p-3 text-[rgb(var(--ala-on-primary))] sm:right-14 sm:p-5">
          <h3 className="font-heading text-sm font-semibold leading-tight sm:text-lg">{member.name}</h3>
          <p className="hidden font-heading text-xs font-light sm:block text-[rgb(var(--ala-on-primary))]/80 sm:text-sm">{member.role}</p>
        </div>
      </div>
      <a
        href={href}
        aria-label={`View ${member.name} bio`}
        className="absolute bottom-0 right-0 flex h-10 w-10 sm:h-14 sm:w-14 items-center justify-center rounded-br-2xl rounded-tl-2xl bg-[rgb(var(--ala-background))] text-[rgb(var(--ala-primary))] shadow-md transition-colors duration-200 hover:bg-[rgb(var(--ala-accent))] hover:text-[rgb(var(--ala-on-primary))]"
      >
        <Plus className="h-[18px] w-[18px] sm:h-[22px] sm:w-[22px] transition-transform duration-300 group-hover:rotate-90" />
      </a>
    </div>
  );
}

function MotionCard({ index, children }: { index: number; children: ReactNode }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{
        type: 'spring',
        stiffness: 90,
        damping: 16,
        delay: (index % 4) * 0.06,
      }}
    >
      {children}
    </motion.div>
  );
}

function toMember(
  id: string,
  data: Record<string, string | null>,
  def: SlotGroupDefinition,
): TeamMember {
  const defaultImage = def.fields.image?.defaultValue;
  return {
    id,
    name: data.name || '',
    role: data.role || '',
    dept: data.dept || '',
    bio: data.bio || '',
    image: data.image || '',
    alt: data.alt || '',
    accent: data.accent || 'from-[rgb(var(--ala-dark))] to-[#1e3a5f]',
    linkedin: data.linkedin || '',
    bioUrl: data.bio_url || '',
    target: data._target || '_blank',
    circleCrop: !!data.image && data.image === defaultImage,
  };
}


export function TeamContent() {
  const [filter, setFilter] = useState<Filter>('All');
  const editMode = useEditMode();

  const m1 = useSlotGroup('teamcontent.member_1', member1Def);
  const m2 = useSlotGroup('teamcontent.member_2', member2Def);
  const m3 = useSlotGroup('teamcontent.member_3', member3Def);
  const m4 = useSlotGroup('teamcontent.member_4', member4Def);
  const m5 = useSlotGroup('teamcontent.member_5', member5Def);
  const m7 = useSlotGroup('teamcontent.member_7', member7Def);
  const m9 = useSlotGroup('teamcontent.member_9', member9Def);
  const m10 = useSlotGroup('teamcontent.member_10', member10Def);
  const m11 = useSlotGroup('teamcontent.member_11', member11Def);
  const m12 = useSlotGroup('teamcontent.member_12', member12Def);
  const m13 = useSlotGroup('teamcontent.member_13', member13Def);
  const m14 = useSlotGroup('teamcontent.member_14', member14Def);
  const m15 = useSlotGroup('teamcontent.member_15', member15Def);
  const m16 = useSlotGroup('teamcontent.member_16', member16Def);
  const m17 = useSlotGroup('teamcontent.member_17', member17Def);
  const m18 = useSlotGroup('teamcontent.member_18', member18Def);
  const m19 = useSlotGroup('teamcontent.member_19', member19Def);
  const m20 = useSlotGroup('teamcontent.member_20', member20Def);
  const m21 = useSlotGroup('teamcontent.member_21', member21Def);
  const m22 = useSlotGroup('teamcontent.member_22', member22Def);
  const m23 = useSlotGroup('teamcontent.member_23', member23Def);
  const m24 = useSlotGroup('teamcontent.member_24', member24Def);
  const m25 = useSlotGroup('teamcontent.member_25', member25Def);
  const m26 = useSlotGroup('teamcontent.member_26', member26Def);
  const m27 = useSlotGroup('teamcontent.member_27', member27Def);
  const m28 = useSlotGroup('teamcontent.member_28', member28Def);
  const m29 = useSlotGroup('teamcontent.member_29', member29Def);
  const m30 = useSlotGroup('teamcontent.member_30', member30Def);
  const m31 = useSlotGroup('teamcontent.member_31', member31Def);
  const m32 = useSlotGroup('teamcontent.member_32', member32Def);
  const m34 = useSlotGroup('teamcontent.member_34', member34Def);
  const m35 = useSlotGroup('teamcontent.member_35', member35Def);
  const m36 = useSlotGroup('teamcontent.member_36', member36Def);
  const m37 = useSlotGroup('teamcontent.member_37', member37Def);
  const m38 = useSlotGroup('teamcontent.member_38', member38Def);
  const m39 = useSlotGroup('teamcontent.member_39', member39Def);
  const m40 = useSlotGroup('teamcontent.member_40', member40Def);
  const m41 = useSlotGroup('teamcontent.member_41', member41Def);
  const m42 = useSlotGroup('teamcontent.member_42', member42Def);
  const m43 = useSlotGroup('teamcontent.member_43', member43Def);
  const m44 = useSlotGroup('teamcontent.member_44', member44Def);
  const m45 = useSlotGroup('teamcontent.member_45', member45Def);
  const m46 = useSlotGroup('teamcontent.member_46', member46Def);
  const m47 = useSlotGroup('teamcontent.member_47', member47Def);
  const m48 = useSlotGroup('teamcontent.member_48', member48Def);
  const m49 = useSlotGroup('teamcontent.member_49', member49Def);

  const members = useMemo(
    () => [
      toMember('1', m1, member1Def),
      toMember('2', m2, member2Def),
      toMember('3', m3, member3Def),
      toMember('4', m4, member4Def),
      toMember('5', m5, member5Def),
      toMember('7', m7, member7Def),
      toMember('9', m9, member9Def),
      toMember('10', m10, member10Def),
      toMember('11', m11, member11Def),
      toMember('12', m12, member12Def),
      toMember('13', m13, member13Def),
      toMember('14', m14, member14Def),
      toMember('15', m15, member15Def),
      toMember('16', m16, member16Def),
      toMember('17', m17, member17Def),
      toMember('18', m18, member18Def),
      toMember('19', m19, member19Def),
      toMember('20', m20, member20Def),
      toMember('21', m21, member21Def),
      toMember('22', m22, member22Def),
      toMember('23', m23, member23Def),
      toMember('24', m24, member24Def),
      toMember('25', m25, member25Def),
      toMember('26', m26, member26Def),
      toMember('27', m27, member27Def),
      toMember('28', m28, member28Def),
      toMember('29', m29, member29Def),
      toMember('30', m30, member30Def),
      toMember('31', m31, member31Def),
      toMember('32', m32, member32Def),
      toMember('34', m34, member34Def),
      toMember('35', m35, member35Def),
      toMember('36', m36, member36Def),
      toMember('37', m37, member37Def),
      toMember('38', m38, member38Def),
      toMember('39', m39, member39Def),
      toMember('40', m40, member40Def),
      toMember('41', m41, member41Def),
      toMember('42', m42, member42Def),
      toMember('43', m43, member43Def),
      toMember('44', m44, member44Def),
      toMember('45', m45, member45Def),
      toMember('46', m46, member46Def),
      toMember('47', m47, member47Def),
      toMember('48', m48, member48Def),
      toMember('49', m49, member49Def),
    ],
    [m1, m2, m3, m4, m5, m7, m9, m10, m11, m12, m13, m14, m15, m16, m17, m18, m19, m20, m21, m22, m23, m24, m25, m26, m27, m28, m29, m30, m31, m32, m34, m35, m36, m37, m38, m39, m40, m41, m42, m43, m44, m45, m46, m47, m48, m49],
  );

  const isVisible = (member: TeamMember) => {
    if (editMode) return true;
    if (!member.name.trim()) return false;
    return filter === 'All' || member.dept === filter;
  };

  let stagger = 0;
  const nextIndex = () => {
    const i = stagger;
    stagger += 1;
    return i;
  };

  return (
    <section className="w-full overflow-x-clip bg-[rgb(var(--ala-surface))] py-16 md:py-24">
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-[clamp(16px,6.7vw,96px)]">
        <div className="mb-10">
          <RichTextContent
            path="teamcontent.headline"
            label="Section Heading"
            as="h2"
            className="max-w-2xl font-heading text-[clamp(33px,3.69vw,60px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-dark))]"
          />
          <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Filter by department">
            {FILTERS.map((item) => {
              const active = filter === item;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => setFilter(item)}
                  aria-pressed={active}
                  className={`rounded-full px-4 py-2 font-heading text-sm font-medium transition-all duration-200 ${
                    active
                      ? 'bg-[rgb(var(--ala-dark))] text-[rgb(var(--ala-on-primary))] shadow-md'
                      : 'border border-[rgb(var(--ala-text))]/10 bg-[rgb(var(--ala-background))] text-[rgb(var(--ala-dark))] hover:border-[rgb(var(--ala-dark))]'
                  }`}
                >
                  {item}
                </button>
              );
            })}
          </div>
        </div>

        <motion.div layout className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {isVisible(members[0]) ? (
              <MotionCard key="1" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_1" definition={member1Def}>
                  <TeamCard member={members[0]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[1]) ? (
              <MotionCard key="2" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_2" definition={member2Def}>
                  <TeamCard member={members[1]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[2]) ? (
              <MotionCard key="3" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_3" definition={member3Def}>
                  <TeamCard member={members[2]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[3]) ? (
              <MotionCard key="4" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_4" definition={member4Def}>
                  <TeamCard member={members[3]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[4]) ? (
              <MotionCard key="5" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_5" definition={member5Def}>
                  <TeamCard member={members[4]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[45]) ? (
              <MotionCard key="49" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_49" definition={member49Def}>
                  <TeamCard member={members[45]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[5]) ? (
              <MotionCard key="7" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_7" definition={member7Def}>
                  <TeamCard member={members[5]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[6]) ? (
              <MotionCard key="9" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_9" definition={member9Def}>
                  <TeamCard member={members[6]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[7]) ? (
              <MotionCard key="10" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_10" definition={member10Def}>
                  <TeamCard member={members[7]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[8]) ? (
              <MotionCard key="11" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_11" definition={member11Def}>
                  <TeamCard member={members[8]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[9]) ? (
              <MotionCard key="12" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_12" definition={member12Def}>
                  <TeamCard member={members[9]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[10]) ? (
              <MotionCard key="13" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_13" definition={member13Def}>
                  <TeamCard member={members[10]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[11]) ? (
              <MotionCard key="14" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_14" definition={member14Def}>
                  <TeamCard member={members[11]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[12]) ? (
              <MotionCard key="15" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_15" definition={member15Def}>
                  <TeamCard member={members[12]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[13]) ? (
              <MotionCard key="16" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_16" definition={member16Def}>
                  <TeamCard member={members[13]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[14]) ? (
              <MotionCard key="17" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_17" definition={member17Def}>
                  <TeamCard member={members[14]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[15]) ? (
              <MotionCard key="18" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_18" definition={member18Def}>
                  <TeamCard member={members[15]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[16]) ? (
              <MotionCard key="19" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_19" definition={member19Def}>
                  <TeamCard member={members[16]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[17]) ? (
              <MotionCard key="20" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_20" definition={member20Def}>
                  <TeamCard member={members[17]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[18]) ? (
              <MotionCard key="21" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_21" definition={member21Def}>
                  <TeamCard member={members[18]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[19]) ? (
              <MotionCard key="22" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_22" definition={member22Def}>
                  <TeamCard member={members[19]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[20]) ? (
              <MotionCard key="23" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_23" definition={member23Def}>
                  <TeamCard member={members[20]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[21]) ? (
              <MotionCard key="24" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_24" definition={member24Def}>
                  <TeamCard member={members[21]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[22]) ? (
              <MotionCard key="25" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_25" definition={member25Def}>
                  <TeamCard member={members[22]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[23]) ? (
              <MotionCard key="26" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_26" definition={member26Def}>
                  <TeamCard member={members[23]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[24]) ? (
              <MotionCard key="27" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_27" definition={member27Def}>
                  <TeamCard member={members[24]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[25]) ? (
              <MotionCard key="28" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_28" definition={member28Def}>
                  <TeamCard member={members[25]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[26]) ? (
              <MotionCard key="29" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_29" definition={member29Def}>
                  <TeamCard member={members[26]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[27]) ? (
              <MotionCard key="30" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_30" definition={member30Def}>
                  <TeamCard member={members[27]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[28]) ? (
              <MotionCard key="31" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_31" definition={member31Def}>
                  <TeamCard member={members[28]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[29]) ? (
              <MotionCard key="32" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_32" definition={member32Def}>
                  <TeamCard member={members[29]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[30]) ? (
              <MotionCard key="34" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_34" definition={member34Def}>
                  <TeamCard member={members[30]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[31]) ? (
              <MotionCard key="35" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_35" definition={member35Def}>
                  <TeamCard member={members[31]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[32]) ? (
              <MotionCard key="36" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_36" definition={member36Def}>
                  <TeamCard member={members[32]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[33]) ? (
              <MotionCard key="37" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_37" definition={member37Def}>
                  <TeamCard member={members[33]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[34]) ? (
              <MotionCard key="38" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_38" definition={member38Def}>
                  <TeamCard member={members[34]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[35]) ? (
              <MotionCard key="39" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_39" definition={member39Def}>
                  <TeamCard member={members[35]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[36]) ? (
              <MotionCard key="40" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_40" definition={member40Def}>
                  <TeamCard member={members[36]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[37]) ? (
              <MotionCard key="41" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_41" definition={member41Def}>
                  <TeamCard member={members[37]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[38]) ? (
              <MotionCard key="42" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_42" definition={member42Def}>
                  <TeamCard member={members[38]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[44]) ? (
              <MotionCard key="48" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_48" definition={member48Def}>
                  <TeamCard member={members[44]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[39]) ? (
              <MotionCard key="43" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_43" definition={member43Def}>
                  <TeamCard member={members[39]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[40]) ? (
              <MotionCard key="44" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_44" definition={member44Def}>
                  <TeamCard member={members[40]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[41]) ? (
              <MotionCard key="45" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_45" definition={member45Def}>
                  <TeamCard member={members[41]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[42]) ? (
              <MotionCard key="46" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_46" definition={member46Def}>
                  <TeamCard member={members[42]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
            {isVisible(members[43]) ? (
              <MotionCard key="47" index={nextIndex()}>
                <EditableSlotGroup groupPath="teamcontent.member_47" definition={member47Def}>
                  <TeamCard member={members[43]} />
                </EditableSlotGroup>
              </MotionCard>
            ) : null}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TeamContent',
  displayName: 'Team Content',
  tokens: {
    'teamcontent.headline': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Meet the leadership team</p>',
    },
  },
  slotGroups: {
    'teamcontent.member_1': member1Def,
    'teamcontent.member_2': member2Def,
    'teamcontent.member_3': member3Def,
    'teamcontent.member_4': member4Def,
    'teamcontent.member_5': member5Def,
    'teamcontent.member_7': member7Def,
    'teamcontent.member_9': member9Def,
    'teamcontent.member_10': member10Def,
    'teamcontent.member_11': member11Def,
    'teamcontent.member_12': member12Def,
    'teamcontent.member_13': member13Def,
    'teamcontent.member_14': member14Def,
    'teamcontent.member_15': member15Def,
    'teamcontent.member_16': member16Def,
    'teamcontent.member_17': member17Def,
    'teamcontent.member_18': member18Def,
    'teamcontent.member_19': member19Def,
    'teamcontent.member_20': member20Def,
    'teamcontent.member_21': member21Def,
    'teamcontent.member_22': member22Def,
    'teamcontent.member_23': member23Def,
    'teamcontent.member_24': member24Def,
    'teamcontent.member_25': member25Def,
    'teamcontent.member_26': member26Def,
    'teamcontent.member_27': member27Def,
    'teamcontent.member_28': member28Def,
    'teamcontent.member_29': member29Def,
    'teamcontent.member_30': member30Def,
    'teamcontent.member_31': member31Def,
    'teamcontent.member_32': member32Def,
    'teamcontent.member_34': member34Def,
    'teamcontent.member_35': member35Def,
    'teamcontent.member_36': member36Def,
    'teamcontent.member_37': member37Def,
    'teamcontent.member_38': member38Def,
    'teamcontent.member_39': member39Def,
    'teamcontent.member_40': member40Def,
    'teamcontent.member_41': member41Def,
    'teamcontent.member_42': member42Def,
    'teamcontent.member_43': member43Def,
    'teamcontent.member_44': member44Def,
    'teamcontent.member_45': member45Def,
    'teamcontent.member_46': member46Def,
    'teamcontent.member_47': member47Def,
    'teamcontent.member_48': member48Def,
    'teamcontent.member_49': member49Def,
  },
};

(TeamContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TeamContent;
